import React, { useMemo, useLayoutEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { useToasts } from 'react-toast-notifications';
import { useTranslation } from 'react-i18next';
import $ from 'jquery';

import * as ROUTES from '../../constants/routes';
import * as STATUS from '../../constants/celebrityStatus';
import * as REASONS from '../../constants/rejectionReasons';
import {
  useCelebrityQuery,
  useSignInAuthorization,
  useFirebase,
} from '../../hooks';
import { getAuthUser } from '../../reducers';
import { deleteCelebrity } from '../../actions';
import { celebrityProfileUrl } from '../../helpers';
import Icon from '../Icon';
import DeleteDialog from '../DeleteDialog';

const REJECTION_REASON_KEYS = {
  [REASONS.INAPPROPRIATE_IMAGE]: 'rejectionReasons.inappropriateImage',
  [REASONS.INCOMPLETE_INCORRECT_INFO]: 'rejectionReasons.incompleteIncorrectInfo',
  [REASONS.DUPLICATE_CELEBRITY]: 'rejectionReasons.duplicateCelebrity',
  [REASONS.OTHER]: 'rejectionReasons.other',
};
export default function MySuggestions() {
  useSignInAuthorization();
  const authUser = useSelector(getAuthUser);
  const { t } = useTranslation();
  const query = useMemo(
    () => ({
      where: ['createdBy', '==', authUser.uid],
      key: 'mySuggestions',
    }),
    [authUser]
  );
  const { celebrities, isFetching, error } = useCelebrityQuery(query);

  const firebase = useFirebase();
  const dispatch = useDispatch();
  const { addToast } = useToasts();
  async function handleDelete(celebrity) {
    try {
      // delete photo if exists
      const photo = celebrity.photo;
      if (typeof photo === 'string') {
        const storage = firebase.storage.ref();
        const file = storage.child(photo);
        await file.delete().catch(error => error);
      }

      await dispatch(deleteCelebrity(celebrity.id));

      addToast(t('account.suggestionsDeleteSuccess', 'The celebrity was deleted.'), {
        appearance: 'success',
        autoDismiss: true,
      });
    } catch (error) {
      addToast(error.message, {
        appearance: 'error',
        autoDismiss: true,
      });
    }
  }

  const [isOpen, setIsOpen] = useState(false);
  const [selectedCelebrity, setSelectedCelebrity] = useState(null);

  function openDeleteDialog(celebrity) {
    setSelectedCelebrity(celebrity);
    setIsOpen(true);
  }

  function toggleDeleteDialog() {
    setIsOpen(!isOpen);
  }

  return (
    <div className="container">
      <div className="row my-3">
        {isFetching ? (
          <div className="col-12 text-center">
            <div
              className="spinner-grow spinner-grow-lg text-primary"
              role="status"
            >
              <span className="sr-only">{t('common.loading')}</span>
            </div>
          </div>
        ) : (
          error && (
            <div className="col-12 text-center">
              <span className="text-danger">{error.message}</span>
            </div>
          )
        )}
        {!isFetching && !celebrities.length && (
          <div
            className="col-12 text-center d-flex align-items-center justify-content-center"
            style={{ minHeight: '350px' }}
          >
            <span className="text-primary">
              {t('account.suggestionsEmpty', 'No suggested celebrity.')}
            </span>
          </div>
        )}
        {!!celebrities.length && (
          <div className="col-12">
            <div className="table-responsive-sm">
              <table className="table">
                <thead>
                  <tr>
                    <th scope="col">#</th>
                    <th scope="col">{t('account.suggestionsName', 'Name')}</th>
                    <th scope="col">{t('account.suggestionsStatus', 'Status')}</th>
                    <th scope="col">{t('account.suggestionsActions', 'Actions')}</th>
                  </tr>
                </thead>
                <tbody>
                  <Celebrities
                    celebrities={celebrities}
                    onDeleteClick={openDeleteDialog}
                  />
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
      <DeleteDialog
        isOpen={isOpen}
        onDelete={() => handleDelete(selectedCelebrity)}
        onToggle={toggleDeleteDialog}
        itemName={
          selectedCelebrity &&
          `${selectedCelebrity.firstname} ${selectedCelebrity.lastname}`
        }
      />
    </div>
  );
}

function Celebrities({ celebrities, onDeleteClick }) {
  useLayoutEffect(() => {
    $(function() {
      $('[data-toggle="popover"]').popover();
    });
  });

  const { t } = useTranslation();

  return celebrities.map((celebrity, idx) => {
    const statusKey =
      celebrity.status === STATUS.APPROVED
        ? 'status.approved'
        : celebrity.status === STATUS.REJECTED
        ? 'status.rejected'
        : 'status.pending';
    const reasonKey = REJECTION_REASON_KEYS[celebrity.rejectionReason];
    return (
    <tr key={celebrity.id}>
      <th className="align-middle" scope="row">
        {idx + 1}
      </th>
      <td className="align-middle">
        {celebrity.status === STATUS.APPROVED ? (
          <Link to={celebrityProfileUrl(celebrity.id, celebrity)}>
            {celebrity.firstname} {celebrity.lastname}
          </Link>
        ) : (
          <span>
            {celebrity.firstname} {celebrity.lastname}
          </span>
        )}
      </td>
      <td className="align-middle">
        {t(statusKey, celebrity.status)}
        {celebrity.status === STATUS.REJECTED && (
          <a
            role="button"
            data-toggle="popover"
            data-trigger="focus"
            title={t('account.suggestionsRejectionTitle', 'Rejection Reason')}
            data-content={t(reasonKey, REASONS[celebrity.rejectionReason])}
            href="#reason"
          >
            <Icon icon={['far', 'question-circle']} className="text-info" />
          </a>
        )}
      </td>
      <td className="align-middle">
        {celebrity.status !== STATUS.APPROVED && (
          <>
            <Link
              className="btn mr-2"
              to={`${ROUTES.CELEBRITY_SUGGEST}/${celebrity.id}`}
            >
              <Icon icon="edit" fixedWidth className="text-dark" size="lg" />
            </Link>
            <button className="btn" onClick={() => onDeleteClick(celebrity)}>
              <Icon
                icon="trash-alt"
                fixedWidth
                className="text-danger"
                size="lg"
              />
            </button>
          </>
        )}
      </td>
    </tr>
  )});
}
